import { Box, Heading, Stack, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import DocumentTitle from '~/components/DocumentTitle';
import LayoutAnonymous from '~/components/LayoutAnonymous';
import Link from '~/components/Link';
import { CONTACT_EMAIL } from '~/constants';
import { SITE_URL } from '~/utils';

const P = (props) => <Text marginBottom={3} {...props} />;
const H2 = (props) => <Heading as="h2" size="lg" marginBottom={2} {...props} />;
const UL = (props) => (
  <Box as="ul" marginBottom={4} paddingLeft={10} {...props} />
);

const TermsPage = () => (
  <LayoutAnonymous>
    <Box padding={5}>
      <DocumentTitle>Terms & Conditions</DocumentTitle>
      <Heading marginBottom={8}>Terms & Conditions</Heading>
      <Stack spacing={6}>
        <Box>
          <P>
            This terms of use agreement (together with the documents referred to
            in it) tells you the terms of use on which you may make use of our
            website{' '}
            <NextLink href="/" legacyBehavior>
              <Link>{SITE_URL}</Link>
            </NextLink>{' '}
            (our site), whether as a guest or a registered user. Use of our site
            includes accessing, browsing, or registering to use our site.
          </P>
          <P>
            Please read these terms of use carefully before you start to use our
            site, as these will apply to your use of our site. We recommend that
            you print a copy of this for future reference.
          </P>
          <P>
            By using our site, you confirm that you accept these terms of use
            and that you agree to comply with them.
          </P>
          <P>
            If you do not agree to these terms of use, you must not use our
            site.
          </P>
        </Box>

        <Box>
          <H2>Other applicable terms</H2>
          <P>
            These terms of use refer to our Privacy Policy, which also applies
            to your use of our site. The Policy is available at{' '}
            <NextLink href="/privacy" legacyBehavior>
              <Link>{`${SITE_URL}/privacy`}</Link>
            </NextLink>{' '}
            and sets out the terms on which we process any personal data we
            collect from you, or that you provide to us. By using our site, you
            consent to such processing and you warrant that all data provided by
            you is accurate.
          </P>
        </Box>
        <Box>
          <H2>Information about us</H2>
          <P>
            {' '}
            <NextLink href="/" legacyBehavior>
              <Link>{SITE_URL}</Link>
            </NextLink>{' '}
            is a site operated by Whalar Limited ("We"). We are registered in
            England and Wales under company number 09803195 and have our
            registered office at 9th Floor, 107 Cheapside, London, United
            Kingdom, EC2V 6DN. Our VAT number is GB223788000. We are a limited
            company.
          </P>
        </Box>
        <Box>
          <H2>Changes to these terms</H2>
          <P>
            We may revise these terms of use at any time by amending this page.
            Please check this page from time to time to take notice of any
            changes we made, as they are binding on you.
          </P>
        </Box>
        <Box>
          <H2>Changes to our site</H2>
          <P>
            We may update our site from time to time, and may change the content
            at any time. However, please note that any of the content on our
            site may be out of date at any given time, and we are under no
            obligation to update it.
          </P>
          <P>
            We do not guarantee that our site, or any content on it, will be
            free from errors or omissions.
          </P>
        </Box>
        <Box>
          <H2>Accessing our site</H2>
          <P>
            We do not guarantee that our site, or any content on it, will always
            be available or be uninterrupted. Access to our site is permitted on
            a temporary basis. We may suspend, withdraw, discontinue or change
            all or any part of our site without notice. We will not be liable to
            you if for any reason our site is unavailable at any time or for any
            period.
          </P>
          <P>
            You are responsible for making all arrangements necessary for you to
            have access to our site.
          </P>
          <P>
            You are also responsible for ensuring that all persons who access
            our site through your internet connection are aware of these terms
            of use and other applicable terms and conditions, and that they
            comply with them. We may limit the availability of our site or any
            service or product described on our site to any person or geographic
            area at any time.
          </P>
          <P>
            In the case where you enter into any paid engagement with Whalar, a
            fifteen percent (15%) platform fee (Echo/Foam) will be deducted as a
            commission from the Creator’s gross compensation. For the avoidance
            of doubt, in the event you are presented with a paid engagement, you
            shall be compensated the full amount presented as a Campaign Fee by
            Whalar.
          </P>
          <P>
            Notwithstanding anything that is in these terms and conditions or
            any permission to the contrary provided while connecting your social
            media accounts to our site, under no circumstances without your
            express permission will Whalar (or any party acting on behalf of
            Whalar) post, remove posts, manage accounts, change personal
            settings, contact third parties (e.g, via DM), or act as you at any
            time.
          </P>
        </Box>
        <Box>
          <H2>Your account and password</H2>
          <P>
            If you choose, or you are provided with, a user identification code,
            password or any other piece of information as part of our security
            procedures, you must treat such information as confidential. You
            must not disclose it to any third party.
          </P>
          <P>
            We have the right to disable any user identification code or
            password, whether chosen by you or allocated by us, at any time, if
            in our reasonable opinion you have failed to comply with any of the
            provisions of these terms of use.
          </P>
          <P>
            If you know or suspect that anyone other than you knows your user
            identification code or password, you must promptly notify us at{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`} isExternal>
              {CONTACT_EMAIL}
            </Link>
          </P>
        </Box>
        <Box>
          <H2>Intellectual property rights</H2>
          <P>
            We are the owner or the licensee of all intellectual property rights
            in our site, and in the material published on it. Those works are
            protected by copyright laws and treaties around the world. All such
            rights are reserved.
          </P>
          <P>
            You may print off one copy, and may download extracts, of any
            page(s) from our site for your personal use and you may draw the
            attention of others within your organisation to content posted on
            our site.
          </P>
          <P>
            You must not modify the paper or digital copies of any materials you
            have printed off or downloaded in any way, and you must not use any
            illustrations, photographs, video or audio sequences or any graphics
            separately from any accompanying text. Our status (and that of any
            identified contributors) as the authors of content on our site must
            always be acknowledged.
          </P>
          <P>
            You must not use any part of the content on our site for commercial
            purposes without obtaining a licence to do so from us or our
            licensors. If you print off, copy or download any part of our site
            in breach of these terms of use, your right to use our site will
            cease immediately and you must, at our option, return or destroy any
            copies of the materials you have made.
          </P>
        </Box>
        <Box>
          <H2>No reliance on information</H2>
          <P>
            The content on our site is provided for general information only. It
            is not intended to amount to advice on which you should rely. You
            must obtain professional or specialist advice before taking, or
            refraining from, any action on the basis of the content on our site.
          </P>
          <P>
            Although we make reasonable efforts to update the information on our
            site, we make no representations, warranties or guarantees, whether
            express or implied, that the content on our site is accurate,
            complete or up-to-date.
          </P>
        </Box>
        <Box>
          <H2>Limitation of our liability</H2>
          <P>
            Nothing in these terms of use excludes or limits our liability for
            death or personal injury arising from our negligence, or our fraud
            or fraudulent misrepresentation, or any other liability that cannot
            be excluded or limited by English law.
          </P>
          <P>
            To the extent permitted by law, we exclude all conditions,
            warranties, representations or other terms which may apply to our
            site or any content on it, whether express or implied.
          </P>
          <P>
            We will not be liable to any user for any loss or damage, whether in
            contract, tort (including negligence), breach of statutory duty, or
            otherwise, even if foreseeable, arising under or in connection with:
          </P>
          <UL>
            <li>use of, or inability to use, our site; or</li>
            <li>use of or reliance on any content displayed on our site.</li>
          </UL>
          <P>
            If you are a business user, please note that in particular, we will
            not be liable for:
          </P>
          <UL>
            <li>loss of profits, sales, business or revenue;</li>
            <li>business interruption;</li>
            <li>loss of anticipated savings;</li>
            loss of business opportunity, goodwill or reputation; or
            <li>any indirect or consequential loss or damage.</li>
          </UL>
          <P>
            If you are a consumer user, please note that we only provide our
            site for domestic and private use. You agree not to use our site for
            any commercial or business purposes, and we have no liability to you
            for any loss of profit, loss of business, business interruption, or
            loss of business opportunity.
          </P>
          <P>
            We will not be liable for any loss or damage caused by a virus,
            distributed denial-of-service attack, or other technologically
            harmful material that may infect your computer equipment, computer
            programs, data or other proprietary material due to your use of our
            site or to your downloading of any content on it, or on any website
            linked to it.
          </P>
          We assume no responsibility for the content of websites linked on our
          site. Such links should not be interpreted as endorsement by us of
          those linked websites. We will not be liable for any loss or damage
          that may arise from your use of them.
          <P>
            Different limitations and exclusions of liability will apply to
            liability arising as a result of the supply of any goods by use to
            you, which will be set out in our Terms and conditions of supply.
          </P>
        </Box>
        <Box>
          <H2>Viruses</H2>
          <P>
            We do not guarantee that our site will be secure or free from bugs
            or viruses.
          </P>
          <P>
            You are responsible for configuring your information technology,
            computer programmes and platform in order to access our site. You
            should use your own virus protection software.
          </P>
          <P>
            You must not misuse our site by knowingly introducing viruses,
            trojans, worms, logic bombs or other material which is malicious or
            technologically harmful. You must not attempt to gain unauthorised
            access to our site, the server on which our site is stored or any
            server, computer or database connected to our site. You must not
            attack our site via a denial-of-service attack or a distributed
            denial-of service attack. By breaching this provision, you would
            commit a criminal offence under the Computer Misuse Act 1990. We
            will report any such breach to the relevant law enforcement
            authorities and we will co-operate with those authorities by
            disclosing your identity to them. In the event of such a breach,
            your right to use our site will cease immediately.
          </P>
        </Box>
        <Box>
          <H2>Cookies</H2>
          <P>
            Our website uses cookies and reserves the right to use cookies in
            the future. A cookie is a small file of letters and numbers that we
            put on your computer if you agree. These cookies allow us to
            distinguish you from other users of our website, which helps us to
            provide you with a good experience when you browse our website and
            also allows us to improve our site. The cookies we use (or may use
            at some stage) are:
          </P>
          <UL>
            <li>
              <strong>Strictly necessary cookies.</strong>
              These are cookies that are required for the operation of our
              website. They include, for example, cookies that enableyou to log
              into secure areas of our website, use a shopping cart or makeuse
              of e-billing services.
            </li>
            <li>
              <strong>Analytical/performance cookies.</strong> They allow us to
              recognise and count the number of visitorsand to see how visitors
              move around our website when they are using it. This helps us to
              improve the way our website works, for example, byensuring that
              users are finding what they are looking for easily.
            </li>
            <li>
              <strong>Functionality cookies. </strong>These are used to
              recognise you when you return to ourwebsite. This enables us to
              personalise our content for you, greet you byname and remember
              your preferences (for example, your choice of languageor region).
            </li>
            <li>
              <strong> Targeting cookies.</strong> These cookies record your
              visit to our website, the pages youhave visited and the links you
              have followed. We will use this informationto make our website and
              the advertising displayed on it more relevant toyour interests. We
              may also share this information with third parties forthis
              purpose.
            </li>
          </UL>
          <P>
            You can block cookies by activating the setting on your browser that
            allows you to refuse the setting of all or some cookies. However, if
            you use your browser settings to block all cookies (including
            essential cookies) you may not be able to access all or parts of our
            site.
          </P>
        </Box>
        <Box>
          <H2>Linking to our site</H2>
          <P>
            You may link to our home page, provided you do so in a way that is
            fair and legal and does not damage our reputation or take advantage
            of it.
          </P>
          <P>
            You must not establish a link in such a way as to suggest any form
            of association, approval or endorsement on our part where none
            exists.
          </P>
          <P>
            You must not establish a link to our site in any website that is not
            owned by you.
          </P>
          <P>Our site must not be framed on any other site.</P>
          <P>
            We reserve the right to withdraw linking permission without notice.
          </P>
          <P>
            If you wish to make any use of content on our site other than that
            set out above, please contact{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`} isExternal>
              {CONTACT_EMAIL}
            </Link>
          </P>
        </Box>
        <Box>
          <H2>Third party links and resources in our site</H2>
          <P>
            Where our site contains links to other sites and resources provided
            by third parties, these links are provided for your information
            only. We have no control over the contents of those sites or
            resources.
          </P>
        </Box>
        <Box>
          <H2>
            Additional Information Concerning Use or Access to YouTube Services
          </H2>
          <P>
            Whalar utilizes the YouTube API Services and by using our site, you
            are agreeing to be bound by: (a) the YouTube Terms of Service (which
            can be found at{' '}
            <Link isExternal href="https://www.youtube.com/t/terms">
              https://www.youtube.com/t/terms
            </Link>
            ) and the Google Privacy Policy (which can be found at{' '}
            <Link isExternal href="http://www.google.com/policies/privacy">
              http://www.google.com/policies/privacy
            </Link>
            ) when engaging YouTube content and services through the site. We
            strongly suggest you review these terms and policies before using
            the site.
            <br />
            In addition to the normal procedure for deleting stored data, you
            will be able to revoke the data of YouTube API Services via the
            Google security settings page at{' '}
            <Link
              href="https://security.google.com/settings/security/permissions"
              isExternal
            >
              https://security.google.com/settings/security/permissions
            </Link>
            .
          </P>
        </Box>
        <Box>
          <H2>Applicable law</H2>
          <P>
            If you are a consumer, please note that these terms of use, its
            subject matter and its formation, are governed by English law. You
            and we both agree to that the courts of England and Wales will have
            non-exclusive jurisdiction. However, if you are a resident of
            Northern Ireland you may also bring proceedings in Northern Ireland,
            and if you are resident of Scotland, you may also bring proceedings
            in Scotland.
          </P>
          <P>
            If you are a business, these terms of use, its subject matter and
            its formation (and any non-contractual disputes or claims) are
            governed by English law. We both agree to the exclusive jurisdiction
            of the courts of England and Wales.
          </P>
        </Box>
        <Box>
          <H2>Contact us</H2>
          <P>
            To contact us, please email{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`} isExternal>
              {CONTACT_EMAIL}
            </Link>
          </P>
          Thank you for visiting our site.
        </Box>
      </Stack>
    </Box>
  </LayoutAnonymous>
);

export default TermsPage;
